import { useEffect, useState } from 'react';

/**
 * Track the viewport-relative bounding rect of an element.
 *
 * Composite observer: the element's own ResizeObserver catches its own
 * size changes; documentElement's ResizeObserver catches layout shifts
 * triggered by anything else on the page (e.g. AI panel toggling).
 * Capture-phase scroll catches scroll on any ancestor — including
 * independently scrollable panes — without needing to know which one.
 *
 * Updates are rAF-throttled so we don't spam setState during rapid
 * scrolls or resizes.
 *
 * Returns `null` while `element` is null (caller can use this as a
 * "not ready" signal).
 */
export function useAnchorRect(element: HTMLElement | null): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!element) {
      setRect(null);
      return;
    }

    let rafId: number | null = null;
    const update = () => {
      rafId = null;
      setRect(element.getBoundingClientRect());
    };
    const schedule = () => {
      if (rafId === null) rafId = requestAnimationFrame(update);
    };

    // Initial measurement
    setRect(element.getBoundingClientRect());

    const elementObserver = new ResizeObserver(schedule);
    elementObserver.observe(element);

    const documentObserver = new ResizeObserver(schedule);
    documentObserver.observe(document.documentElement);

    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      elementObserver.disconnect();
      documentObserver.disconnect();
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
    };
  }, [element]);

  return rect;
}
